import { Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';

/** The classes of client-side failure the beacon records. Mirrors the server's allowlist. */
export type GableClientErrorKind =
    | 'stream_failed'
    | 'stream_stalled'
    | 'stream_gap'
    | 'channel_auth_failed'
    | 'http_error'
    | 'drawer_error';

/** Not a failure: the drawer telling core a completed turn actually reached the screen. */
const TURN_DELIVERED = 'turn_delivered';

export interface GableClientErrorReport {
    context?: string;
    url?: string;
    detail?: string;
    conversationId?: number;
    messageId?: number;
}

const MESSAGE_CAP = 500;
const CONTEXT_CAP = 120;
const DETAIL_CAP = 1000;

/** Same report inside this window is dropped, so a looping drawer sends one beacon, not hundreds. */
const DEDUPE_WINDOW_MS = 60_000;

/** Hard ceiling per page load, after which the reporter goes quiet until the next navigation. */
const MAX_REPORTS_PER_SESSION = 20;

/** Confirmations get their own, larger budget: one per turn is the normal shape, not a symptom. */
const MAX_CONFIRMATIONS_PER_SESSION = 200;

/** How many confirmed turn ids to remember, so a re-render never confirms the same turn twice. */
const CONFIRMED_TURNS_REMEMBERED = 200;

/**
 * Posts Gable's own drawer failures to core (SD-8146), plus a confirmation that
 * a completed turn actually reached the screen, so the admin Health page's
 * Browser layer has a real signal instead of a blind spot.
 *
 * The confirmation closes the last hop: core marks a turn complete, publishes it
 * and never learns whether it landed. Ids and counters only — never any of the
 * turn's content.
 *
 * Deliberately NOT a global error handler: this reports the class of failure the
 * customer experiences as "Gable broke", not every unrelated error in the app.
 *
 * Fire-and-forget by contract — every failure path here is swallowed, nothing is
 * retried, and no caller ever waits on the result. A beacon that surfaced its own
 * problems would be a second error on an already-broken page.
 */
@Injectable({ providedIn: 'root' })
export class GableClientErrorReporterService {
    private _sent = 0;
    private _confirmed = 0;
    private readonly _recent = new Map<string, number>();
    private readonly _confirmedTurns: number[] = [];

    constructor(private readonly _http: ApplicationHttpClient) {}

    /**
     * Confirm that a completed turn rendered. Called once per turn from the
     * drawer's terminal state — never on the first token, which proves only that
     * the stream opened.
     */
    confirmTurnDelivered(conversationId: number | null, messageId: number): void {
        try {
            if (
                this._confirmed >= MAX_CONFIRMATIONS_PER_SESSION
                || messageId <= 0
                || this._confirmedTurns.includes(messageId)
            ) {
                return;
            }
            this._confirmedTurns.push(messageId);
            if (this._confirmedTurns.length > CONFIRMED_TURNS_REMEMBERED) {
                this._confirmedTurns.shift();
            }
            this._confirmed += 1;
            this._post(TURN_DELIVERED, '', {
                context: 'gable-drawer:ai.stream.completed',
                conversationId: conversationId ?? undefined,
                messageId,
            });
        } catch {
            // A confirmation is never worth an error of its own.
        }
    }

    report(kind: GableClientErrorKind, message: string, report: GableClientErrorReport = {}): void {
        try {
            this._send(kind, message, report);
        } catch {
            // Reporting must never become the failure it is reporting.
        }
    }

    private _send(kind: GableClientErrorKind, message: string, report: GableClientErrorReport): void {
        if (this._sent >= MAX_REPORTS_PER_SESSION) {
            return;
        }

        const body = this._truncate(message, MESSAGE_CAP) || 'Unknown Gable client error';
        const key = `${kind}|${body}`;
        const now = Date.now();
        const last = this._recent.get(key);
        if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
            return;
        }
        this._recent.set(key, now);

        this._sent += 1;
        this._post(kind, body, report);
    }

    private _post(kind: string, message: string, report: GableClientErrorReport): void {
        const orgId = LegFiJwtService.read()?.orgId;
        if (typeof orgId !== 'number') {
            return;
        }

        this._http.post(
            Routes.MakeLegFiCoreUrl(Routes.GableModule.ClientErrors(orgId)),
            this._http.serializeObject({
                kind,
                message,
                context: this._truncate(report.context ?? '', CONTEXT_CAP),
                url: report.url ?? window.location.pathname,
                detail: this._truncate(report.detail ?? '', DETAIL_CAP),
                conversationId: report.conversationId ?? null,
                messageId: report.messageId ?? null,
            }),
        ).pipe(catchError(() => EMPTY)).subscribe();
    }

    private _truncate(value: string, cap: number): string {
        return value.length > cap ? value.slice(0, cap) : value;
    }
}
